"use client";

import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { postContact } from "@/apis/queryFunctions/contact";
import { useToast } from "@/context/ToastProvider";
import { PROJECT_INFO } from "@/types/projectConsts";
import { cn } from "@/utils/cn";
import ActionButton from "@/components/Buttons/ActionButton";

const contactSchema = z.object({
  ho_ten: z.string().trim().min(1, "Vui lòng nhập họ tên của quý khách"),
  dien_thoai: z
    .string()
    .trim()
    .min(1, "Vui lòng nhập số điện thoại")
    .regex(
      /^[+0-9\s]{9,}$/,
      "Số điện thoại tối thiểu 9 số, chỉ nhận ký tự số, khoảng trắng và dấu +",
    ),
  email: z
    .string()
    .trim()
    .email("Email không hợp lệ")
    .optional()
    .or(z.literal("")),
  loai_can_ho: z.string().trim().min(1, "Vui lòng chọn loại hình quan tâm"),
  b_url: z.string().optional(),
});

type ContactFormData = z.infer<typeof contactSchema>;

interface SharedContactFormProps {
  theme: "light" | "dark";
  defaultProduct?: string;
  apptOptions?: { label: string; value: string }[];
  submitButtonText?: string;
  nguon?: string;
  onSuccess?: () => void;
  useDropdown?: boolean;
  size?: "default" | "compact";
}

export default function SharedContactForm({
  theme,
  defaultProduct = "",
  apptOptions = [
    { label: "1PN", value: "1PN" },
    { label: "2PN", value: "2PN" },
    { label: "Studio", value: "Studio" },
  ],
  submitButtonText = "NHẬN BÁO GIÁ NGAY",
  nguon = "Đăng ký tư vấn",
  onSuccess,
  useDropdown = false,
  size = "default",
}: SharedContactFormProps) {
  const { showToast } = useToast();

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      ho_ten: "",
      dien_thoai: "",
      email: "",
      loai_can_ho: defaultProduct || apptOptions?.[0]?.value || "",
      b_url: "",
    },
  });

  const selectedLoaiCanHo = watch("loai_can_ho");

  // Sync default selection based on matching defaultProduct or fallback to first option
  useEffect(() => {
    if (defaultProduct) {
      const match = apptOptions.find(
        (opt) =>
          opt.value.toLowerCase().includes(defaultProduct.toLowerCase()) ||
          opt.label.toLowerCase().includes(defaultProduct.toLowerCase()) ||
          defaultProduct.toLowerCase().includes(opt.value.toLowerCase()) ||
          defaultProduct.toLowerCase().includes(opt.label.toLowerCase()),
      );
      if (match) {
        setValue("loai_can_ho", match.value);
      } else {
        const exists = apptOptions.some(
          (opt) => opt.value === defaultProduct || opt.label === defaultProduct,
        );
        if (exists) {
          setValue("loai_can_ho", defaultProduct);
        } else {
          setValue("loai_can_ho", apptOptions[0]?.value || defaultProduct);
        }
      }
    } else if (apptOptions && apptOptions.length > 0) {
      setValue("loai_can_ho", apptOptions[0].value);
    }
  }, [defaultProduct, apptOptions, setValue]);

  const contactMutation = useMutation({
    mutationFn: postContact,
    onSuccess: (rs: any) => {
      if (rs) {
        showToast(
          "Cảm ơn Quý khách. Dragon Home sẽ liên hệ tư vấn trong thời gian sớm nhất.",
          "success",
        );
        reset({
          ho_ten: "",
          dien_thoai: "",
          email: "",
          loai_can_ho: defaultProduct || apptOptions?.[0]?.value || "",
          b_url: "",
        });
        if (onSuccess) onSuccess();
      } else {
        showToast(
          "Gửi thông tin không thành công. Vui lòng thử lại sau.",
          "error",
        );
      }
    },
    onError: () => {
      showToast("Có lỗi xảy ra, vui lòng thử lại sau.", "error");
    },
  });

  const onSubmit = (data: ContactFormData) => {
    if (data.b_url) {
      // Spam honeypot triggered
      showToast(
        "Cảm ơn Quý khách. Dragon Home sẽ liên hệ tư vấn trong thời gian sớm nhất.",
        "success",
      );
      reset({
        ho_ten: "",
        dien_thoai: "",
        email: "",
        loai_can_ho: defaultProduct || apptOptions?.[0]?.value || "",
        b_url: "",
      });
      if (onSuccess) onSuccess();
      return;
    }
    const payload = {
      ho_ten: data.ho_ten,
      dien_thoai: data.dien_thoai,
      email: data.email || "",
      dia_chi: "",
      noi_dung: `Loại hình quan tâm: ${data.loai_can_ho}`,
      loai_can_ho: data.loai_can_ho,
      loai_hinh: data.loai_can_ho, // backwards compatibility
      nguon,
    };
    contactMutation.mutate(payload);
  };

  // Styling maps based on theme
  const isLight = theme === "light";
  const isCompact = size === "compact";

  const inputClass = isLight
    ? `w-full bg-transparent border-0 border-b border-gray-300 rounded-none pl-0 pr-0 ${isCompact || useDropdown ? "py-1 text-xs" : "py-2 text-sm"} focus:outline-none focus:border-b-primary focus:ring-0 text-black transition-all duration-500 placeholder:text-gray-400 placeholder:font-light`
    : `w-full bg-transparent border-0 border-b border-white/25 rounded-none px-0 ${isCompact ? "py-1 text-xs" : "py-2 text-sm"} text-white focus:outline-none focus:border-b-secondary focus:ring-0 transition duration-300 placeholder:text-white/40 placeholder:font-light`;

  const labelClass = isLight
    ? `block text-black text-[9px] font-bold uppercase tracking-widest opacity-70 group-focus-within:text-primary group-focus-within:opacity-100 transition-all duration-300 ${isCompact || useDropdown ? "mb-0.5" : ""}`
    : `block text-white ${isCompact ? "text-[9px] mb-0.5" : "text-[10px] mb-1.5"} font-bold uppercase tracking-widest opacity-80 group-focus-within:text-secondary transition duration-300`;

  const errorClass = isLight
    ? "mt-0.5 text-[9px] text-red-600 font-medium"
    : `mt-0.5 font-medium text-xs text-red-300`;

  const selectClass = isLight
    ? `w-full bg-white text-gray-800 px-3 ${isCompact || useDropdown ? "py-1.5 text-xs" : "py-2 text-xs"} rounded border border-gray-300 outline-none focus:ring-1 focus:ring-primary focus:border-primary transition cursor-pointer font-medium`
    : `w-full bg-white text-gray-800 border border-gray-200 ${isCompact || useDropdown ? "px-3 py-1.5 rounded-md text-xs" : "px-4 py-2.5 rounded-lg text-sm"} outline-none transition duration-200 focus:border-primary focus:ring-2 focus:ring-primary/20 cursor-pointer`;

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={isCompact ? "space-y-4" : "space-y-6"}
    >
      {/* Honeypot */}
      <div className="hidden" aria-hidden="true">
        <input
          type="text"
          tabIndex={-1}
          autoComplete="off"
          placeholder="Do not fill this field"
          {...register("b_url")}
        />
      </div>

      {/* HỌ TÊN */}
      <div className="relative group">
        <label className={labelClass}>
          Họ tên của bạn{" "}
          <span className={isLight ? "text-secondary-hover" : "text-white/80"}>
            *
          </span>
        </label>
        <input
          type="text"
          placeholder="Ví dụ: Nguyễn Văn A"
          {...register("ho_ten")}
          className={inputClass}
        />
        {errors.ho_ten && <p className={errorClass}>{errors.ho_ten.message}</p>}
      </div>

      {/* SỐ ĐIỆN THOẠI */}
      <div className="relative group">
        <label className={labelClass}>
          Số điện thoại{" "}
          <span className={isLight ? "text-secondary-hover" : "text-white/80"}>
            *
          </span>
        </label>
        <input
          type="text"
          placeholder="Ví dụ: 0912345678"
          {...register("dien_thoai")}
          className={inputClass}
        />
        {errors.dien_thoai && (
          <p className={errorClass}>{errors.dien_thoai.message}</p>
        )}
      </div>

      {/* EMAIL */}
      <div className="relative group">
        <label className={labelClass}>
          {isLight
            ? "Địa chỉ Email (Không bắt buộc)"
            : "Địa chỉ Email (Không bắt buộc)"}
        </label>
        <input
          type="text"
          placeholder="Ví dụ: example@gmail.com"
          {...register("email")}
          className={inputClass}
        />
        {errors.email && <p className={errorClass}>{errors.email.message}</p>}
      </div>

      {/* LOẠI HÌNH QUAN TÂM */}
      <div className="relative group">
        <label className={labelClass}>
          Loại hình quan tâm{" "}
          <span className={isLight ? "text-primary" : "text-white/80"}>*</span>
        </label>

        {useDropdown ? (
          <select
            {...register("loai_can_ho")}
            className={cn(selectClass, "text-gray-800 bg-white")}
          >
            {apptOptions.map((opt) => (
              <option
                key={opt.value}
                value={opt.value}
                className="text-gray-800 bg-white"
              >
                {opt.label}
              </option>
            ))}
          </select>
        ) : (
          <div
            className={
              isCompact ? "grid grid-cols-2 gap-2" : "grid grid-cols-2 gap-3"
            }
          >
            {apptOptions.map((opt) => {
              const active =
                selectedLoaiCanHo === opt.value ||
                selectedLoaiCanHo === opt.label;
              return (
                <label
                  key={opt.value}
                  className={
                    isLight
                      ? `
                        flex items-center cursor-pointer select-none transition duration-200
                        ${isCompact ? "p-2 rounded-md gap-1.5 text-[11px]" : "p-2.5 rounded-lg gap-2 text-xs"}
                        ${
                          active
                            ? "bg-primary border-primary/50 text-white"
                            : "bg-white border-gray-200 text-gray-700 hover:bg-gray-50"
                        }
                      `
                      : `
                         block text-center cursor-pointer ${isCompact ? "py-2 text-[10px]" : "py-2.5 text-[11px]"} transition duration-300 uppercase tracking-wider border rounded-none select-none
                         ${
                           active
                             ? "border-secondary bg-secondary text-primaryDark font-bold"
                             : "border-white/20 text-[#d1d5db] bg-transparent hover:bg-white/5"
                         }
                       `
                  }
                >
                  <input
                    type="radio"
                    value={opt.value}
                    checked={active}
                    onChange={() => setValue("loai_can_ho", opt.value)}
                    className="sr-only"
                  />
                  {isLight && (
                    <span
                      className={`rounded-full border flex items-center justify-center ${isCompact ? "w-3 h-3" : "w-3.5 h-3.5"} ${
                        active ? "border-secondary/50" : "border-gray-300"
                      }`}
                    >
                      {active && (
                        <span
                          className={`rounded-full ${isCompact ? "w-1 h-1" : "w-1.5 h-1.5"} bg-secondary`}
                        />
                      )}
                    </span>
                  )}
                  <span>{opt.label}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>

      <div className={isCompact ? "pt-2" : "pt-4"}>
        <ActionButton
          type="submit"
          disabled={contactMutation.isPending}
          label={contactMutation.isPending ? "ĐANG GỬI..." : submitButtonText}
          className="w-full"
          variant={isLight ? "primary" : "secondary"}
        />
      </div>
    </form>
  );
}
